import {useEffect, useRef, type CSSProperties, type ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {MapPin} from 'lucide-react';
import {cn} from '@chekinapp/ui';
import {useArrivalProperty} from 'components/arrival';
import {useBrandingTheme} from 'context/brandingTheme';
import {useReservation} from 'context/reservation';
import {useSummary} from 'context/summary';
import {useStaySummary} from 'hooks/useStaySummary';
import {prefersReducedMotion} from 'utils/prefersReducedMotion';
import {
  FIELD_START,
  FIELD_STEP,
  SKELETON_OFF_S,
  VALUE_IN_S,
  VALUE_TICK_S,
} from './bookingCardTiming';
import {formatStayDate} from './formatStayDate';
import {delayAfter, useLateStart} from './useLateStart';
import styles from './entrance.module.css';

const VALUE_FLASH_MS = 650;
const VALUE_FLASH_EASE = 'cubic-bezier(.22,1,.36,1)';
const VALUE_FLASH_COLOR = '#6be0a8';

function useFlashOnChange(value: string | undefined, isEnabled: boolean) {
  const valueRef = useRef<HTMLElement>(null);
  const previousValue = useRef(value);

  useEffect(
    function flashTheNewValue() {
      const hasChanged = previousValue.current && previousValue.current !== value;
      previousValue.current = value;

      if (!hasChanged || !isEnabled || prefersReducedMotion()) return;

      valueRef.current?.animate(
        [{color: VALUE_FLASH_COLOR, transform: 'scale(1.08)'}, {transform: 'none'}],
        {duration: VALUE_FLASH_MS, easing: VALUE_FLASH_EASE},
      );
    },
    [value, isEnabled],
  );

  return valueRef;
}

type FieldProps = {
  label: string;
  value?: string;
  flashOnChange?: boolean;
  index: number;
  startedAt: number;
  startDelay: number;
};

function CardField({
  label,
  value,
  flashOnChange = false,
  index,
  startedAt,
  startDelay,
}: FieldProps) {
  const late = useLateStart(Boolean(value), startedAt);
  const valueRef = useFlashOnChange(value, flashOnChange);
  const d = FIELD_START + startDelay + index * FIELD_STEP;

  return (
    <div
      className={styles.field}
      style={
        {
          '--d': `${d}s`,
          '--off': delayAfter(d + SKELETON_OFF_S, late),
          '--v': delayAfter(d + VALUE_IN_S, late),
          '--tick': `${VALUE_TICK_S}s`,
        } as CSSProperties
      }
    >
      <small>{label}</small>
      <span className={cn(styles.skeleton, value && styles.done)} />
      {value && <b ref={valueRef}>{value}</b>}
    </div>
  );
}

type BookingCardField = {
  label: string;
  value?: string;
  flashOnChange?: boolean;
};

type BookingCardProps = {
  name: string;
  location?: string;
  logo?: string | null;
  fields: BookingCardField[];
  startedAt: number;
  startDelay?: number;
  className?: string;
  children?: ReactNode;
};

function BookingCard({
  name,
  location,
  logo,
  fields,
  startedAt,
  startDelay = 0,
  className,
  children,
}: BookingCardProps) {
  return (
    <div className={cn(styles.card, className)} data-entrance="card">
      <div className={styles.cardHead}>
        <span className={cn(styles.logo, logo && styles.withImage)} aria-hidden>
          {logo ? <img src={logo} alt="" /> : name.charAt(0)}
        </span>
        <span className="min-w-0">
          <b className={styles.cardName}>{name}</b>
          {location && (
            <small className={styles.cardMeta}>
              <MapPin size={12} aria-hidden />
              {location}
            </small>
          )}
        </span>
      </div>
      <i className={styles.rule} />
      <div className={styles.grid}>
        {fields.map((field, index) => (
          <CardField
            key={field.label}
            label={field.label}
            value={field.value}
            flashOnChange={field.flashOnChange}
            index={index}
            startedAt={startedAt}
            startDelay={startDelay}
          />
        ))}
      </div>
      {children}
    </div>
  );
}

type Props = {
  startedAt: number;
};

function EntranceBookingCard({startedAt}: Props) {
  const {t, i18n} = useTranslation();
  const stay = useStaySummary();
  const property = useArrivalProperty();
  const {data: branding} = useBrandingTheme();
  const {isLoading: isReservationLoading} = useReservation();
  const {isLoading: isSummaryLoading} = useSummary();
  const isStayLoading = Boolean(isReservationLoading || isSummaryLoading);

  const name = stay.propertyName || property.name || '';
  const location = [property.city, property.country].filter(Boolean).join(', ');
  const logo = branding?.splash_screen_logo;
  const checkIn = formatStayDate(stay.checkInDate, i18n.language);
  const checkOut = formatStayDate(stay.checkOutDate, i18n.language);

  const fields = [
    {label: t('entrance_booking'), value: stay.bookingRef || undefined},
    {
      label: t('entrance_stay'),
      value: checkIn && checkOut ? `${checkIn} – ${checkOut}` : undefined,
    },
    {
      label: t('guests'),
      value: stay.guests ? t('guests_count', {count: stay.guests}) : undefined,
    },
    {
      label: t('entrance_nights'),
      value: stay.nights ? t('nights_count', {count: stay.nights}) : undefined,
    },
  ].filter(field => isStayLoading || field.value);

  return (
    <div className={styles.content} data-entrance="content">
      <BookingCard
        name={name}
        location={location}
        logo={logo}
        fields={fields}
        startedAt={startedAt}
      >
        <p className={styles.opening}>
          {t('entrance_opening_booking')}
          <i />
        </p>
      </BookingCard>
    </div>
  );
}

export {BookingCard, EntranceBookingCard};
